import { createRouter, createWebHistory, createWebHashHistory, RouteRecordRaw } from 'vue-router'
import store from '../store'
import { userCan } from '../composables/usePermission'
import { openInFocusMode } from '../composables/useFocusModePreference'
import UnavailableView from '../components/shared/UnavailableView.vue'

// Chatwoot foi removido de vez (ver PLANO_MIGRACAO_WOOTDASH.md). Rotas cujo
// dado inteiro vinha do Chatwoot (direto ou via n8n, que por baixo só lia a API
// dele) ficam com uma tela de aviso em vez de silenciosamente quebrar — cada
// uma volta quando tiver um endpoint de verdade no WootDash (Fase 2/3).
const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    name: 'dashboard',
    component: () => import('../views/DashboardView.vue'),
  },
  {
    path: '/foco',
    name: 'foco',
    component: () => import('../views/FocoLiveView.vue'),
    meta: { permKey: 'foco.view' },
  },
  {
    path: '/templates',
    name: 'templates',
    component: () => import('../views/TemplatesView.vue'),
    meta: { permKey: 'templates.view' },
  },
  {
    path: '/resumo',
    name: 'resumo',
    component: UnavailableView,
    meta: { permKey: 'resumo.view' },
    props: {
      title: 'Resumo indisponível',
      reason: 'Os números vinham de conversas do Chatwoot via n8n. Sem endpoint de relatório no WootDash ainda (Fase 2).',
    },
  },
  {
    path: '/relatorio-resolucao',
    name: 'relatorio-resolucao',
    component: () => import('../views/RelatorioResolucaoView.vue'),
    meta: { permKey: 'relatorioResolucao.view' },
  },
  {
    path: '/contacts',
    name: 'contacts',
    component: () => import('../views/ContactsView.vue'),
    meta: { permKey: 'contacts.view' },
  },
  {
    path: '/sla',
    name: 'sla',
    component: () => import('../views/SLAView.vue'),
    meta: { permKey: 'sla.view' },
  },
  {
    path: '/teams',
    name: 'teams',
    component: () => import('../views/TeamsView.vue'),
    meta: { permKey: 'teams.view' },
  },
  {
    path: '/macros',
    name: 'macros',
    component: () => import('../views/MacrosView.vue'),
    meta: { permKey: 'macros.view' },
  },
  {
    // Editor de bots (plano-fluxos-bot.md, Fase 3) — rascunho/publicação em /bots.
    path: '/fluxos-bot',
    name: 'fluxos-bot',
    component: () => import('../views/BotFlowMockView.vue'),
    // remount: mesmo componente do fluxo do ciclo — sem isso, ir de um pro outro reaproveitava a tela com o fluxo errado.
    meta: { permKey: 'bot.view', remount: true },
  },
  {
    path: '/etiquetas',
    name: 'etiquetas',
    component: () => import('../views/LabelsView.vue'),
    meta: { permKey: 'etiquetas.view' },
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('../views/AdminView.vue'),
    meta: { permKey: 'admin.view' },
  },
  {
    // Inteligência Artificial (agentes, base de conhecimento, aprendizado): saiu do Admin pro menu lateral.
    path: '/ia',
    redirect: '/ia/agentes',
  },
  {
    path: '/ia/:page',
    name: 'ia',
    component: () => import('../views/IaView.vue'),
    meta: { permKey: 'ai.view' },
  },
  {
    path: '/sla-metro',
    name: 'sla-metro',
    component: UnavailableView,
    meta: { permKey: 'slaMetro.view' },
    props: {
      title: 'SLA Telão indisponível',
      reason: 'Fora de escopo por decisão explícita — ver PLANO_SUBSTITUICAO_CHATWOOT.md.',
    },
  },
  {
    path: '/avaliacoes',
    name: 'avaliacoes',
    component: () => import('../views/AvaliacoesView.vue'),
    meta: { permKey: 'avaliacoes.view' },
  },
  {
    path: '/fila',
    name: 'fila',
    component: () => import('../views/FilaView.vue'),
    meta: { permKey: 'fila.view' },
  },
  {
    // Ciclos de envio automático (plano-ciclos-envio.md) — ciclos.view.
    path: '/ciclos',
    name: 'ciclos',
    component: () => import('../views/CiclosView.vue'),
    meta: { permKey: 'ciclos.view' },
  },
  {
    // Fluxo agendado de um ciclo (plano-fluxo-agendado.md fase 7): o mesmo editor do fluxo de bot, salvando no ciclo.
    path: '/ciclos/:id/fluxo',
    name: 'ciclo-fluxo',
    component: () => import('../views/BotFlowMockView.vue'),
    props: route => ({ cycleId: String(route.params.id) }),
    meta: { permKey: 'ciclos.view', remount: true },
  },
  {
    // Relatórios da operação (plano-relatorios.md): fila, horário, equipe e mais (relatorios.view).
    path: '/relatorios',
    name: 'relatorios',
    component: () => import('../views/RelatoriosView.vue'),
    meta: { permKey: 'relatorios.view' },
  },
  {
    // Métricas: desempenho por agente, bots e resultados da operação (metricas.view — Administrador, Supervisor, Sistema).
    path: '/metricas',
    name: 'metricas',
    component: () => import('../views/MetricasView.vue'),
    meta: { permKey: 'metricas.view' },
  },
  {
    // Consumo do servidor (CPU, memória, rede e disco) — por padrão só o perfil Sistema (monitoring.view).
    path: '/consumo',
    name: 'consumo',
    component: () => import('../views/ConsumoView.vue'),
    meta: { permKey: 'monitoring.view' },
  },
  {
    path: '/agentes',
    name: 'agentes',
    component: () => import('../views/AgentesView.vue'),
    meta: { permKey: 'agentes.view' },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('../views/ProfileView.vue'),
  },
]

const router = createRouter({
  history: process.env.IS_ELECTRON
    ? createWebHashHistory()
    : createWebHistory(process.env.BASE_URL),
  routes,
})

// "Abrir em Modo Foco" (preferência salva no Perfil, ver useFocusModePreference.ts):
// landing padrão do login e da rota "/" vira /foco em vez de /, pra não disparar
// o motor pesado do Dashboard (useDashboardEngine, que busca conversas de todos
// os agentes/times) quando o usuário só quer ver as próprias conversas.
function landingRouteName(): 'foco' | 'dashboard' {
  if (!openInFocusMode.value) return 'dashboard'
  const teamIds = (store.getters.userTeamIds ?? []) as number[]
  return userCan(store.getters.authUser, teamIds, 'foco.view') ? 'foco' : 'dashboard'
}

router.beforeEach((to, _from, next) => {
  const loggedIn = store.getters.isLoggedIn

  if (!to.meta.public && !loggedIn) {
    next({ name: 'login' })
    return
  }
  if (to.name === 'login' && loggedIn) {
    next({ name: landingRouteName() })
    return
  }
  if (to.name === 'dashboard' && loggedIn) {
    const landing = landingRouteName()
    if (landing !== 'dashboard') {
      next({ name: landing })
      return
    }
  }

  // Page-level permission check
  const permKey = to.meta.permKey as string | undefined
  if (permKey && loggedIn) {
    const teamIds = (store.getters.userTeamIds ?? []) as number[]
    if (!userCan(store.getters.authUser, teamIds, permKey)) {
      next({ name: 'dashboard' })
      return
    }
  }

  next()
})

export default router
